import { Show, type JSX, type ParentProps } from 'solid-js';
import { authStore } from '@stores/session';

// A simple interstitial. Mounted once the user has a profile; blocks access
// to medical surfaces until the GDPR + medical disclaimer is acknowledged.
// The acceptance is written to profiles.accepted_disclaimer_at so it survives
// across devices and we can re-prompt on material policy changes.
export default function DisclaimerGate(props: ParentProps): JSX.Element {
  const needsConsent = () => {
    const p = authStore.profile();
    return !!p && !p.accepted_disclaimer_at;
  };

  return (
    <Show when={!needsConsent()} fallback={<DisclaimerModal />}>
      {props.children}
    </Show>
  );
}

function DisclaimerModal(): JSX.Element {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="disclaimer-title"
      class="fixed inset-0 bg-brand-ink/40 backdrop-blur-sm flex items-center justify-center p-4 z-50"
    >
      <div class="bg-brand-cream rounded-2xl max-w-lg p-6 shadow-xl">
        <h2 id="disclaimer-title" class="font-display text-2xl font-bold">
          Before you begin
        </h2>
        <div class="mt-4 space-y-3 text-sm">
          <p>
            <strong>Terp Track is not a medical service.</strong> It is an informational patient
            support tool to help you track personal medication efficacy. It does not provide
            medical advice, diagnosis, or prescriptions.
          </p>
          <p>
            Your session logs are treated as UK GDPR <em>Special Category Data</em>. Notes are
            encrypted on your device before being sent to our servers, and your identity is
            separated from your health data at the database layer.
          </p>
          <p>
            We never process patient prescription labels. Only publicly-available manufacturer
            Certificates of Analysis (COAs) may be uploaded.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void authStore.acceptDisclaimer()}
          class="mt-6 w-full rounded-full bg-brand-green text-brand-cream py-3 font-medium"
        >
          I understand &ndash; continue
        </button>
      </div>
    </div>
  );
}
